import { useEffect, useState } from "react";
import { errorLogger } from "@/Common/helpers/logger";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { IWorkingDays } from "@/Settings/types/Common/model";
import { Stack } from "@mui/material";
import { useMutation } from "@tanstack/react-query";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import InfoSubTitle from "../../../common/InfoSubTitle";

const multiSelectDaysData = [
	{ label: "Monday" },
	{ label: "Tuesday" },
	{ label: "Wednesday" },
	{ label: "Thursday" },
	{ label: "Friday" },
	{ label: "Saturday" },
	{ label: "Sunday" },
];

const orderSelectedDays = (selectedDays: string[] = []) => {
	const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
	return selectedDays.sort((a, b) => days.indexOf(a) - days.indexOf(b));
};

type TWorkingDaysProps = {
	locationId: string;
	position: string;
	workingDays?: IWorkingDays[];
};

const WorkingDays = (props: TWorkingDaysProps) => {
	const { locationId, position, workingDays = [] } = props;
	const [selectedWorkingDays, setSelectedWorkingDays] = useState<{ position: string; workingDays: string[] }>({
		position: "",
		workingDays: [],
	});

	const { mutate, isPending } = useMutation({
		mutationFn: (input: { position: string; workingDays: string[] }) =>
			updateSystemSettings(locationId, { workingDays: input }, "WORKING_DAYS"),
		onError: (error) => errorLogger(error),
	});

	const handleSelectDays = (selected: { label: string }[] = []) => {
		const orderedSelectedDays = orderSelectedDays(selected?.map?.((item) => item?.label));
		if (JSON.stringify(selectedWorkingDays.workingDays) === JSON.stringify(orderedSelectedDays)) {
			return;
		}
		const updatedSettings = {
			position,
			workingDays: orderedSelectedDays,
		};
		setSelectedWorkingDays(updatedSettings);
		mutate(updatedSettings);
	};

	const stringWorkingDays = JSON.stringify(workingDays);
	useEffect(() => {
		const foundWorkingDays = workingDays?.find((element) => element.position === position) ?? { position: "", workingDays: [] };
		setSelectedWorkingDays(foundWorkingDays);
		return () => {
			setSelectedWorkingDays({ position: "", workingDays: [] });
		};
	}, [stringWorkingDays]);

	return (
		<Stack>
			<InfoSubTitle title="Working Days" className="!mb-[24px]" />
			<SmartSelector
				multiple
				multipleSelectAll
				tagsVariant={"text"}
				selectBy={"label"}
				showBy={"label"}
				options={multiSelectDaysData}
				selected={selectedWorkingDays.workingDays.map((item) => ({ label: item }))}
				listBoxProps={{
					sx: { maxHeight: "220px" },
				}}
				clearable
				disabled={isPending}
				error={selectedWorkingDays.workingDays.length === 0}
				sx={{ width: "281px" }}
				onChangeAction={(values) => handleSelectDays(values as { label: string }[])}
			/>
		</Stack>
	);
};

export default WorkingDays;
